/**
 * Email templates for the CTA engine.
 * NO mascot — uses company logo only.
 * PII must be masked: use CANDIDATE_NAME = alias (e.g. Candidate A).
 *
 * Placeholder tokens:
 *   {{CANDIDATE_NAME}}  — MUST be alias (PII masked)
 *   {{CANDIDATE_SKILLS}}, {{CANDIDATE_YEARS_EXP}}, {{CANDIDATE_CURRENT_ROLE}}, {{CANDIDATE_EDUCATION}}
 *   {{JOB_TITLE}}, {{CLIENT_NAME}}, {{RECRUITER_NAME}}, {{ORG_NAME}}
 *   {{ACCEPT_URL}}, {{REJECT_URL}}, {{QUERY_URL}}, {{CTA_URL}}, {{TRACK_PIXEL}}
 */

const LOGO_URL = 'https://yzqamouiptmylhbmkxqs.supabase.co/storage/v1/object/public/images/Logo.png';

export type TemplateKey =
  | 'first_submission'
  | 'followup_day3'
  | 'followup_day7'
  | 'interview_confirmation'
  | 'interview_candidate_invite'
  | 'interview_panel_calendar_hold'
  | 'post_interview'
  /** AI browser interview — prep link & session context (candidate). */
  | 'ai_interview_prep_invite_candidate'
  | 'ai_interview_reminder_candidate'
  | 'ai_interview_followup_candidate'
  /** After client feedback — outcomes to candidate. */
  | 'candidate_outcome_accept'
  | 'candidate_outcome_reject'
  | 'candidate_outcome_next_round';

export interface TemplateVars {
  CANDIDATE_NAME: string;
  CANDIDATE_SKILLS?: string;
  CANDIDATE_YEARS_EXP?: string;
  CANDIDATE_CURRENT_ROLE?: string;
  CANDIDATE_EDUCATION?: string;
  CUSTOM_MESSAGE?: string;
  JOB_TITLE: string;
  CLIENT_NAME: string;
  RECRUITER_NAME: string;
  ORG_NAME: string;
  /** Unique ref for subject line (e.g. GGJ-2025-CLNT-ABC123) */
  REF_ID?: string;
  /** §9.13 — panel / interviewer greeting */
  PANELIST_NAME?: string;
  INTERVIEW_DATE?: string;
  INTERVIEW_ROUND?: string;
  INTERVIEW_LOCATION?: string;
  /** AI prep workspace URL (tokenised) — use only in candidate-facing AI templates. */
  PREP_URL?: string;
  /** e.g. Screening / Technical / Behavioural */
  SESSION_TYPE_LABEL?: string;
  /** Short note from recruiter on client feedback (optional). */
  CLIENT_FEEDBACK_SUMMARY?: string;
  /** Hiring company (client org) — use in candidate-facing templates; distinct from client contact salutation. */
  EMPLOYER_NAME?: string;
  [key: string]: string | undefined;
}

/** EML-030: documented merge field catalogue for UI helpers */
export const MERGE_FIELDS: { token: string; label: string; required?: boolean }[] = [
  { token: '{{CANDIDATE_NAME}}',         label: 'Candidate name (alias)',    required: true },
  { token: '{{JOB_TITLE}}',              label: 'Job title',                 required: true },
  { token: '{{CLIENT_NAME}}',            label: 'Client / company name',     required: true },
  { token: '{{RECRUITER_NAME}}',         label: 'Recruiter name',            required: true },
  { token: '{{ORG_NAME}}',              label: 'Agency / org name',          required: true },
  { token: '{{CANDIDATE_SKILLS}}',       label: 'Candidate top skills' },
  { token: '{{CANDIDATE_YEARS_EXP}}',    label: 'Years of experience' },
  { token: '{{CANDIDATE_CURRENT_ROLE}}', label: 'Current role/title' },
  { token: '{{CANDIDATE_EDUCATION}}',    label: 'Education summary' },
  { token: '{{CUSTOM_MESSAGE}}',         label: 'Custom inline message' },
  { token: '{{REF_ID}}',                 label: 'Reference ID (e.g. GGJ-ABC)' },
  { token: '{{PREP_URL}}',               label: 'AI interview prep link (candidate)' },
  { token: '{{SESSION_TYPE_LABEL}}',     label: 'AI session type (e.g. Screening)' },
  { token: '{{CLIENT_FEEDBACK_SUMMARY}}', label: 'Client feedback summary (short)' },
  { token: '{{EMPLOYER_NAME}}',            label: 'Hiring company (client org)' },
  { token: '{{INTERVIEW_DATE}}',          label: 'Interview date & time (scheduled)' },
  { token: '{{INTERVIEW_LOCATION}}',     label: 'Interview location or link' },
  { token: '{{INTERVIEW_ROUND}}',        label: 'Interview round (e.g. Round 1)' },
  { token: '{{ACCEPT_URL}}',             label: 'Client accept link (CTA)' },
  { token: '{{REJECT_URL}}',             label: 'Client reject link (CTA)' },
  { token: '{{QUERY_URL}}',              label: 'Client query link (CTA)' },
  { token: '{{TRACK_PIXEL}}',            label: 'Open-tracking pixel (auto)' },
];

function baseLayout(content: string): string {
  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>GoGrabJob Business</title>
</head>
<body style="margin:0;padding:0;background:#0f0f10;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;">
  <table width="100%" cellpadding="0" cellspacing="0" style="background:#0f0f10;padding:32px 16px;">
    <tr><td align="center">
      <table width="100%" style="max-width:560px;background:#1a1a1e;border-radius:12px;border:1px solid #2a2a30;overflow:hidden;">
        <!-- Header: Company logo only (no mascot) -->
        <tr>
          <td style="background:#1a1a1e;padding:24px 28px;text-align:center;border-bottom:1px solid #2a2a30;">
            <img src="${LOGO_URL}" alt="GoGrabJob" width="140" style="display:inline-block;border:none;outline:none;text-decoration:none;" />
          </td>
        </tr>
        <!-- Body -->
        <tr><td style="padding:28px;">
          ${content}
        </td></tr>
        <!-- Footer -->
        <tr>
          <td style="padding:16px 28px;border-top:1px solid #2a2a30;">
            <p style="margin:0;color:#666;font-size:11px;line-height:1.5;">
              This email was sent by <strong style="color:#aaa;">{{ORG_NAME}}</strong> via GoGrabJob Business.
              If you believe this was sent in error, please ignore this email.
            </p>
          </td>
        </tr>
      </table>
    </td></tr>
  </table>
  {{TRACK_PIXEL}}
</body>
</html>`;
}

function ctaButtons(): string {
  return `
    <table cellpadding="0" cellspacing="0" style="margin-top:24px;">
      <tr>
        <td style="padding-right:8px;">
          <a href="{{ACCEPT_URL}}" style="display:inline-block;padding:10px 20px;background:#22c55e;color:#fff;font-size:13px;font-weight:600;border-radius:8px;text-decoration:none;">
            ✓ Shortlist
          </a>
        </td>
        <td style="padding-right:8px;">
          <a href="{{REJECT_URL}}" style="display:inline-block;padding:10px 20px;background:#3f3f46;color:#e4e4e7;font-size:13px;font-weight:600;border-radius:8px;text-decoration:none;">
            ✗ Pass
          </a>
        </td>
        <td>
          <a href="{{QUERY_URL}}" style="display:inline-block;padding:10px 20px;background:#3f3f46;color:#e4e4e7;font-size:13px;font-weight:600;border-radius:8px;text-decoration:none;">
            ? Query
          </a>
        </td>
      </tr>
    </table>
    <p style="color:#666;font-size:11px;margin-top:12px;">
      Or respond directly: <a href="{{CTA_URL}}" style="color:#6d47ff;">open response form ↗</a>
    </p>`;
}

const TEMPLATES: Record<TemplateKey, { subject: string; body: (vars: TemplateVars) => string }> = {

  first_submission: {
    subject: 'Profile Submission — {{CANDIDATE_NAME}} for {{JOB_TITLE}} · {{REF_ID}}',
    body: (v) => baseLayout(`
      <p style="color:#e4e4e7;font-size:15px;margin:0 0 4px;">Hi {{CLIENT_NAME}},</p>
      <p style="color:#a1a1aa;font-size:13px;margin:0 0 20px;">from {{RECRUITER_NAME}} · {{ORG_NAME}}</p>

      <p style="color:#e4e4e7;font-size:14px;line-height:1.7;margin:0 0 16px;">
        Please find below the profile of <strong style="color:#fff;">{{CANDIDATE_NAME}}</strong>
        being submitted for the role of <strong style="color:#6d47ff;">{{JOB_TITLE}}</strong>.
      </p>

      <div style="background:#111113;border:1px solid #2a2a30;border-radius:8px;padding:16px 20px;margin:0 0 16px;">
        <p style="color:#a1a1aa;font-size:11px;margin:0 0 8px;text-transform:uppercase;letter-spacing:.5px;">Candidate Summary (PII masked)</p>
        <p style="color:#fff;font-size:14px;font-weight:600;margin:0 0 8px;">{{CANDIDATE_NAME}}</p>
        <table cellpadding="0" cellspacing="0" style="font-size:12px;color:#a1a1aa;line-height:1.6;">
          <tr><td style="padding:2px 12px 2px 0;color:#6b7280;width:90px;">Role</td><td>{{CANDIDATE_CURRENT_ROLE}}</td></tr>
          <tr><td style="padding:2px 12px 2px 0;color:#6b7280;">Experience</td><td>{{CANDIDATE_YEARS_EXP}}</td></tr>
          <tr><td style="padding:2px 12px 2px 0;color:#6b7280;">Skills</td><td>{{CANDIDATE_SKILLS}}</td></tr>
          <tr><td style="padding:2px 12px 2px 0;color:#6b7280;">Education</td><td>{{CANDIDATE_EDUCATION}}</td></tr>
        </table>
        <p style="color:#6b7280;font-size:11px;margin:12px 0 0;">Submitted via GoGrabJob Business</p>
      </div>

      <p style="color:#e4e4e7;font-size:14px;line-height:1.7;margin:0 0 8px;">
        Please review the attached profile and let us know your decision using the buttons below.
      </p>
      {{CUSTOM_MESSAGE}}
      ${ctaButtons()}
    `),
  },

  followup_day3: {
    subject: 'Following up — {{CANDIDATE_NAME}} for {{JOB_TITLE}} · {{REF_ID}}',
    body: (v) => baseLayout(`
      <p style="color:#e4e4e7;font-size:15px;margin:0 0 4px;">Hi {{CLIENT_NAME}},</p>
      <p style="color:#a1a1aa;font-size:13px;margin:0 0 20px;">from {{RECRUITER_NAME}} · {{ORG_NAME}}</p>

      <p style="color:#e4e4e7;font-size:14px;line-height:1.7;margin:0 0 16px;">
        Just a quick follow-up regarding the profile of <strong style="color:#fff;">{{CANDIDATE_NAME}}</strong>
        for <strong style="color:#6d47ff;">{{JOB_TITLE}}</strong> that we shared earlier.
      </p>

      <p style="color:#a1a1aa;font-size:14px;line-height:1.7;margin:0 0 16px;">
        Have you had a chance to review it? Your feedback will help us move quickly on next steps.
      </p>
      ${ctaButtons()}
    `),
  },

  followup_day7: {
    subject: 'Last follow-up — {{CANDIDATE_NAME}} for {{JOB_TITLE}} · {{REF_ID}}',
    body: (v) => baseLayout(`
      <p style="color:#e4e4e7;font-size:15px;margin:0 0 4px;">Hi {{CLIENT_NAME}},</p>
      <p style="color:#a1a1aa;font-size:13px;margin:0 0 20px;">from {{RECRUITER_NAME}} · {{ORG_NAME}}</p>

      <p style="color:#e4e4e7;font-size:14px;line-height:1.7;margin:0 0 16px;">
        This is our final follow-up regarding <strong style="color:#fff;">{{CANDIDATE_NAME}}</strong>
        for <strong style="color:#6d47ff;">{{JOB_TITLE}}</strong>.
      </p>

      <p style="color:#a1a1aa;font-size:14px;line-height:1.7;margin:0 0 16px;">
        If the role is no longer active or the timing doesn't work out, we completely understand — please
        let us know so we can update our records and reach out when you have a future requirement.
      </p>
      ${ctaButtons()}
    `),
  },

  interview_confirmation: {
    subject: 'Interview Confirmation — {{CANDIDATE_NAME}} · {{JOB_TITLE}} · {{REF_ID}}',
    body: (v) => baseLayout(`
      <p style="color:#e4e4e7;font-size:15px;margin:0 0 4px;">Hi {{CLIENT_NAME}},</p>
      <p style="color:#a1a1aa;font-size:13px;margin:0 0 20px;">from {{RECRUITER_NAME}} · {{ORG_NAME}}</p>

      <p style="color:#e4e4e7;font-size:14px;line-height:1.7;margin:0 0 16px;">
        Great news — <strong style="color:#fff;">{{CANDIDATE_NAME}}</strong> has been scheduled for an interview
        for the <strong style="color:#6d47ff;">{{JOB_TITLE}}</strong> position.
      </p>

      <div style="background:#111113;border:1px solid #2a2a30;border-radius:8px;padding:16px 20px;margin:0 0 20px;">
        <p style="color:#a1a1aa;font-size:11px;margin:0 0 8px;text-transform:uppercase;letter-spacing:.5px;">Next Steps</p>
        <p style="color:#e4e4e7;font-size:13px;line-height:1.6;margin:0;">
          Please confirm the interview slot with your team. Our candidate has been notified and is prepared.
        </p>
      </div>

      <p style="color:#a1a1aa;font-size:13px;line-height:1.6;margin:0 0 16px;">
        If you have any questions or need to reschedule, please reach out to us directly or use the link below.
      </p>
      <p style="margin:0;">
        <a href="{{CTA_URL}}" style="display:inline-block;padding:10px 20px;background:#6d47ff;color:#fff;font-size:13px;font-weight:600;border-radius:8px;text-decoration:none;">
          Update Status / Send Query ↗
        </a>
      </p>
    `),
  },

  /** §9.13 — sent to candidate when a slot is committed (email queue / worker). */
  interview_candidate_invite: {
    subject: 'Your interview — {{JOB_TITLE}} · {{INTERVIEW_DATE}} · {{REF_ID}}',
    body: () => baseLayout(`
      <p style="color:#e4e4e7;font-size:15px;margin:0 0 4px;">Hi {{CANDIDATE_NAME}},</p>
      <p style="color:#a1a1aa;font-size:13px;margin:0 0 20px;">{{ORG_NAME}} · {{RECRUITER_NAME}}</p>

      <p style="color:#e4e4e7;font-size:14px;line-height:1.7;margin:0 0 16px;">
        Your interview for <strong style="color:#6d47ff;">{{JOB_TITLE}}</strong>
        with <strong style="color:#e4e4e7;">{{CLIENT_NAME}}</strong> is confirmed.
      </p>

      <div style="background:#111113;border:1px solid #2a2a30;border-radius:8px;padding:16px 20px;margin:0 0 20px;">
        <p style="color:#a1a1aa;font-size:11px;margin:0 0 10px;text-transform:uppercase;letter-spacing:.5px;">When &amp; where</p>
        <p style="color:#e4e4e7;font-size:14px;margin:0 0 6px;"><strong>Time:</strong> {{INTERVIEW_DATE}}</p>
        <p style="color:#e4e4e7;font-size:14px;margin:0 0 6px;"><strong>Round:</strong> {{INTERVIEW_ROUND}}</p>
        <p style="color:#e4e4e7;font-size:14px;margin:0;"><strong>Location / link:</strong> {{INTERVIEW_LOCATION}}</p>
      </div>

      <p style="color:#a1a1aa;font-size:13px;line-height:1.6;margin:0;">
        If you need to reschedule, reply to this email or contact <strong style="color:#e4e4e7;">{{RECRUITER_NAME}}</strong>.
      </p>
    `),
  },

  /** §9.13 — calendar hold for lead interviewer / panelist (no client CTA). */
  interview_panel_calendar_hold: {
    subject: 'Interview hold — {{CANDIDATE_NAME}} · {{JOB_TITLE}} · {{INTERVIEW_DATE}}',
    body: () => baseLayout(`
      <p style="color:#e4e4e7;font-size:15px;margin:0 0 4px;">Hi {{PANELIST_NAME}},</p>
      <p style="color:#a1a1aa;font-size:13px;margin:0 0 20px;">{{ORG_NAME}} · {{RECRUITER_NAME}}</p>

      <p style="color:#e4e4e7;font-size:14px;line-height:1.7;margin:0 0 16px;">
        Please hold your calendar for an interview with <strong style="color:#fff;">{{CANDIDATE_NAME}}</strong>
        for <strong style="color:#6d47ff;">{{JOB_TITLE}}</strong> ({{CLIENT_NAME}}).
      </p>

      <div style="background:#111113;border:1px solid #2a2a30;border-radius:8px;padding:16px 20px;margin:0 0 16px;">
        <p style="color:#e4e4e7;font-size:14px;margin:0 0 6px;"><strong>Time:</strong> {{INTERVIEW_DATE}}</p>
        <p style="color:#e4e4e7;font-size:14px;margin:0 0 6px;"><strong>Round:</strong> {{INTERVIEW_ROUND}}</p>
        <p style="color:#e4e4e7;font-size:14px;margin:0;"><strong>Location / link:</strong> {{INTERVIEW_LOCATION}}</p>
      </div>
    `),
  },

  post_interview: {
    subject: 'Post-Interview Update Request — {{CANDIDATE_NAME}} · {{REF_ID}}',
    body: (v) => baseLayout(`
      <p style="color:#e4e4e7;font-size:15px;margin:0 0 4px;">Hi {{CLIENT_NAME}},</p>
      <p style="color:#a1a1aa;font-size:13px;margin:0 0 20px;">from {{RECRUITER_NAME}} · {{ORG_NAME}}</p>

      <p style="color:#e4e4e7;font-size:14px;line-height:1.7;margin:0 0 16px;">
        We hope the interview with <strong style="color:#fff;">{{CANDIDATE_NAME}}</strong>
        for <strong style="color:#6d47ff;">{{JOB_TITLE}}</strong> went well.
      </p>

      <p style="color:#a1a1aa;font-size:14px;line-height:1.7;margin:0 0 16px;">
        Could you share the outcome so we can update our records and take the next steps accordingly?
      </p>
      ${ctaButtons()}
    `),
  },

  /** AI interview — invite candidate to prep workspace after scheduling / pipeline link. */
  ai_interview_prep_invite_candidate: {
    subject: 'Your AI interview — {{JOB_TITLE}} ({{SESSION_TYPE_LABEL}}) · {{REF_ID}}',
    body: () => baseLayout(`
      <p style="color:#e4e4e7;font-size:15px;margin:0 0 4px;">Hi {{CANDIDATE_NAME}},</p>
      <p style="color:#a1a1aa;font-size:13px;margin:0 0 20px;">{{ORG_NAME}} · {{RECRUITER_NAME}}</p>

      <p style="color:#e4e4e7;font-size:14px;line-height:1.7;margin:0 0 16px;">
        Your <strong style="color:#6d47ff;">{{SESSION_TYPE_LABEL}}</strong> AI interview
        for <strong style="color:#fff;">{{JOB_TITLE}}</strong> with <strong style="color:#e4e4e7;">{{EMPLOYER_NAME}}</strong> is set up.
        Please use the secure prep link below when you are ready — it works in your browser; no app install required.
      </p>

      <div style="margin:20px 0;text-align:center;">
        <a href="{{PREP_URL}}" style="display:inline-block;padding:12px 24px;background:#6d47ff;color:#fff;font-size:14px;font-weight:600;border-radius:8px;text-decoration:none;">
          Open AI interview prep
        </a>
      </div>

      <p style="color:#a1a1aa;font-size:13px;line-height:1.6;margin:0 0 16px;">
        If the link does not open, copy and paste this URL into your browser:<br/>
        <span style="color:#6d47ff;word-break:break-all;font-size:12px;">{{PREP_URL}}</span>
      </p>

      {{CUSTOM_MESSAGE}}

      <p style="color:#71717a;font-size:12px;line-height:1.6;margin:16px 0 0;">
        Questions? Reply to this email or contact <strong style="color:#e4e4e7;">{{RECRUITER_NAME}}</strong>.
      </p>
    `),
  },

  ai_interview_reminder_candidate: {
    subject: 'Reminder — complete your AI interview ({{SESSION_TYPE_LABEL}}) · {{JOB_TITLE}} · {{REF_ID}}',
    body: () => baseLayout(`
      <p style="color:#e4e4e7;font-size:15px;margin:0 0 4px;">Hi {{CANDIDATE_NAME}},</p>
      <p style="color:#a1a1aa;font-size:13px;margin:0 0 20px;">{{ORG_NAME}} · {{RECRUITER_NAME}}</p>

      <p style="color:#e4e4e7;font-size:14px;line-height:1.7;margin:0 0 16px;">
        This is a friendly reminder to complete your <strong style="color:#6d47ff;">{{SESSION_TYPE_LABEL}}</strong> AI interview
        for <strong style="color:#fff;">{{JOB_TITLE}}</strong> when you have a quiet 20–30 minutes.
      </p>

      <div style="margin:20px 0;text-align:center;">
        <a href="{{PREP_URL}}" style="display:inline-block;padding:12px 24px;background:#6d47ff;color:#fff;font-size:14px;font-weight:600;border-radius:8px;text-decoration:none;">
          Continue prep / start interview
        </a>
      </div>
      {{CUSTOM_MESSAGE}}
    `),
  },

  ai_interview_followup_candidate: {
    subject: 'Following up — your AI interview ({{JOB_TITLE}}) · {{REF_ID}}',
    body: () => baseLayout(`
      <p style="color:#e4e4e7;font-size:15px;margin:0 0 4px;">Hi {{CANDIDATE_NAME}},</p>
      <p style="color:#a1a1aa;font-size:13px;margin:0 0 20px;">{{ORG_NAME}} · {{RECRUITER_NAME}}</p>

      <p style="color:#e4e4e7;font-size:14px;line-height:1.7;margin:0 0 16px;">
        We wanted to follow up on your <strong style="color:#6d47ff;">{{SESSION_TYPE_LABEL}}</strong> AI interview
        for <strong style="color:#fff;">{{JOB_TITLE}}</strong>. If you have not finished yet, you can use the same link below.
      </p>

      <div style="margin:20px 0;text-align:center;">
        <a href="{{PREP_URL}}" style="display:inline-block;padding:12px 24px;background:#6d47ff;color:#fff;font-size:14px;font-weight:600;border-radius:8px;text-decoration:none;">
          Open AI interview
        </a>
      </div>

      <p style="color:#a1a1aa;font-size:13px;line-height:1.6;margin:0;">
        If you are no longer interested or need a new link, please reply and we will help.
      </p>
      {{CUSTOM_MESSAGE}}
    `),
  },

  candidate_outcome_accept: {
    subject: 'Great news — next steps on {{JOB_TITLE}} · {{REF_ID}}',
    body: () => baseLayout(`
      <p style="color:#e4e4e7;font-size:15px;margin:0 0 4px;">Hi {{CANDIDATE_NAME}},</p>
      <p style="color:#a1a1aa;font-size:13px;margin:0 0 20px;">{{ORG_NAME}} · {{RECRUITER_NAME}}</p>

      <p style="color:#e4e4e7;font-size:14px;line-height:1.7;margin:0 0 16px;">
        We have received positive feedback from <strong style="color:#fff;">{{EMPLOYER_NAME}}</strong> regarding your recent interview
        for <strong style="color:#6d47ff;">{{JOB_TITLE}}</strong>. We would like to move forward with you.
      </p>

      <div style="background:#14532d33;border:1px solid #22c55e55;border-radius:8px;padding:14px 18px;margin:0 0 16px;">
        <p style="color:#86efac;font-size:12px;margin:0 0 6px;text-transform:uppercase;letter-spacing:.5px;">Summary</p>
        <p style="color:#e4e4e7;font-size:14px;margin:0;line-height:1.6;">{{CLIENT_FEEDBACK_SUMMARY}}</p>
      </div>

      {{CUSTOM_MESSAGE}}

      <p style="color:#71717a;font-size:12px;line-height:1.6;margin:16px 0 0;">
        We will share concrete next steps shortly. Reply to this email if you have any questions.
      </p>
    `),
  },

  candidate_outcome_reject: {
    subject: 'Update on your application — {{JOB_TITLE}} · {{REF_ID}}',
    body: () => baseLayout(`
      <p style="color:#e4e4e7;font-size:15px;margin:0 0 4px;">Hi {{CANDIDATE_NAME}},</p>
      <p style="color:#a1a1aa;font-size:13px;margin:0 0 20px;">{{ORG_NAME}} · {{RECRUITER_NAME}}</p>

      <p style="color:#e4e4e7;font-size:14px;line-height:1.7;margin:0 0 16px;">
        Thank you for investing your time in the process         for <strong style="color:#6d47ff;">{{JOB_TITLE}}</strong> with
        <strong style="color:#fff;">{{EMPLOYER_NAME}}</strong>. After careful review of the feedback, we will not be moving forward
        with your candidacy for this role at this time.
      </p>

      <div style="background:#1a1a1e;border:1px solid #3f3f46;border-radius:8px;padding:14px 18px;margin:0 0 16px;">
        <p style="color:#a1a1aa;font-size:12px;margin:0 0 6px;">Feedback note (where available)</p>
        <p style="color:#d4d4d8;font-size:13px;margin:0;line-height:1.6;">{{CLIENT_FEEDBACK_SUMMARY}}</p>
      </div>

      {{CUSTOM_MESSAGE}}

      <p style="color:#71717a;font-size:12px;line-height:1.6;margin:16px 0 0;">
        We genuinely appreciate your interest and wish you the best in your search. Please stay in touch for future opportunities.
      </p>
    `),
  },

  candidate_outcome_next_round: {
    subject: 'Next round — {{JOB_TITLE}} · {{REF_ID}}',
    body: () => baseLayout(`
      <p style="color:#e4e4e7;font-size:15px;margin:0 0 4px;">Hi {{CANDIDATE_NAME}},</p>
      <p style="color:#a1a1aa;font-size:13px;margin:0 0 20px;">{{ORG_NAME}} · {{RECRUITER_NAME}}</p>

      <p style="color:#e4e4e7;font-size:14px;line-height:1.7;margin:0 0 16px;">
        Good news — <strong style="color:#fff;">{{EMPLOYER_NAME}}</strong> would like to proceed with a further round for
        <strong style="color:#6d47ff;">{{JOB_TITLE}}</strong>. Below is a quick summary; we will follow up with scheduling details.
      </p>

      <div style="background:#111113;border:1px solid #2a2a30;border-radius:8px;padding:16px 20px;margin:0 0 16px;">
        <p style="color:#a1a1aa;font-size:11px;margin:0 0 8px;text-transform:uppercase;letter-spacing:.5px;">Round &amp; timing</p>
        <p style="color:#e4e4e7;font-size:14px;margin:0 0 6px;"><strong>Round:</strong> {{INTERVIEW_ROUND}}</p>
        <p style="color:#e4e4e7;font-size:14px;margin:0 0 6px;"><strong>Proposed / scheduled:</strong> {{INTERVIEW_DATE}}</p>
        <p style="color:#e4e4e7;font-size:14px;margin:0;"><strong>Location / link:</strong> {{INTERVIEW_LOCATION}}</p>
      </div>

      <div style="background:#1e1b4b33;border:1px solid #6366f155;border-radius:8px;padding:14px 18px;margin:0 0 16px;">
        <p style="color:#a5b4fc;font-size:12px;margin:0 0 6px;">Client feedback snapshot</p>
        <p style="color:#e4e4e7;font-size:13px;margin:0;line-height:1.6;">{{CLIENT_FEEDBACK_SUMMARY}}</p>
      </div>

      {{CUSTOM_MESSAGE}}
    `),
  },
};

export function getTemplate(key: TemplateKey): { subject: string; body: (vars: TemplateVars) => string } {
  return TEMPLATES[key];
}

export function renderTemplate(key: TemplateKey, vars: TemplateVars): { subject: string; bodyHtml: string } {
  const tpl = TEMPLATES[key];
  // Interpolate vars into subject (undefined → '—')
  const subject = Object.entries(vars).reduce(
    (s, [k, v]) => s.replaceAll(`{{${k}}}`, v ?? '—'),
    tpl.subject
  );
  // Build HTML body (vars interpolation happens both here and in injectEmailTokens)
  let bodyHtml = tpl.body(vars);
  bodyHtml = Object.entries(vars).reduce(
    (b, [k, v]) => b.replaceAll(`{{${k}}}`, v ?? '—'),
    bodyHtml
  );
  return { subject, bodyHtml };
}

/** EML-020: merge {{TOKEN}} placeholders for DB-stored template HTML/subject. */
export function applyTemplateVarsToText(text: string, vars: TemplateVars): string {
  return Object.entries(vars).reduce((s, [k, v]) => s.replaceAll(`{{${k}}}`, v ?? '—'), text);
}

export const TEMPLATE_LABELS: Record<TemplateKey, string> = {
  first_submission:               'First Submission',
  followup_day3:                  'Follow-up (Day 3)',
  followup_day7:                  'Follow-up (Day 7 — Final)',
  interview_confirmation:         'Interview Confirmation (client)',
  interview_candidate_invite:      'Interview — candidate invite',
  interview_panel_calendar_hold:   'Interview — panel calendar hold',
  post_interview:                 'Post-Interview Update Request',
  ai_interview_prep_invite_candidate: 'AI interview — prep invite (candidate)',
  ai_interview_reminder_candidate:    'AI interview — reminder (candidate)',
  ai_interview_followup_candidate:    'AI interview — follow-up (candidate)',
  candidate_outcome_accept:           'Outcome — accept / proceed (candidate)',
  candidate_outcome_reject:           'Outcome — not proceeding (candidate)',
  candidate_outcome_next_round:         'Outcome — next round (candidate)',
};
